import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InvoiceDialog } from '@/components/invoices/invoice-dialog';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import { Receipt, AlertTriangle } from 'lucide-react';

const invoiceStatusColors: Record<string, string> = {
  draft: 'bg-gray-500/10 text-gray-400',
  sent: 'bg-blue-500/10 text-blue-400',
  paid: 'bg-dyndev-green/10 text-dyndev-green',
  overdue: 'bg-red-500/10 text-red-400',
  cancelled: 'bg-yellow-500/10 text-yellow-400',
  awaiting_transfer: 'bg-amber-500/10 text-amber-400',
  refunded: 'bg-purple-500/10 text-purple-400',
};

const invoiceStatusLabels: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  awaiting_transfer: 'Awaiting Transfer',
  refunded: 'Refunded',
};

/** Clients see "Due" instead of the internal "sent" workflow state. */
function invoiceStatusLabel(status: string, isClient: boolean): string {
  if (isClient && status === 'sent') return 'Due';
  return invoiceStatusLabels[status] || status;
}

interface ProjectInvoicesTabProps {
  isClient: boolean;
  invoices: any[];
  invoicesLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  pagination: { totalPages: number; total: number } | null;
}

export function ProjectInvoicesTab({
  isClient,
  invoices,
  invoicesLoading,
  page,
  onPageChange,
  pagination,
}: ProjectInvoicesTabProps) {
  // Deposit outstanding flag (A6) — derived from invoice state, no stored field
  const depositOutstanding = invoices.some(
    (inv: any) =>
      inv.quoteId &&
      ['sent', 'awaiting_transfer', 'overdue'].includes(inv.status),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-[var(--text-secondary)]">
            {pagination?.total || 0} invoice
            {(pagination?.total || 0) !== 1 ? 's' : ''}
          </h3>
          {depositOutstanding && !isClient && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-medium border border-amber-500/20">
              <AlertTriangle size={10} />
              Deposit outstanding
            </span>
          )}
        </div>
        {!isClient && <InvoiceDialog key={page} />}
      </div>

      {invoicesLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-24 rounded-lg bg-[var(--border-color)] animate-pulse"
            />
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Receipt
            size={32}
            className="text-[var(--text-secondary)] mb-3"
          />
          <p className="text-sm font-medium mb-1">
            No invoices for this project
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            {isClient
              ? 'Invoices will appear here once created.'
              : 'Create an invoice to bill this project.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv: any) => (
            <div
              key={inv.id}
              className="flex items-center gap-4 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-dyndev-green/20 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium">
                    {inv.invoiceNumber}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px] px-1.5 py-0',
                      invoiceStatusColors[inv.status]
                    )}
                  >
                    {invoiceStatusLabel(inv.status, isClient)}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]">
                  <span>
                    {inv.client?.companyName ||
                      inv.client?.user?.name ||
                      'Unknown'}
                  </span>
                  {inv.dueDate && (
                    <span>Due: {formatDate(inv.dueDate)}</span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold">
                  {formatCurrency(inv.total)}
                </p>
                {inv._count?.lineItems > 0 && (
                  <p className="text-[10px] text-[var(--text-secondary)]">
                    {inv._count.lineItems} item
                    {inv._count.lineItems !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-[var(--text-secondary)]">
            Page {page} of {pagination.totalPages}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
